import { Body1, Body2, Button, Subtitle1, useMedia, XStack } from "@medbillai/ui";
import { formatCurrency } from "@medbillai/utils/currency";

// Closed-case result banner: six locked-copy outcomes, green surface for
// positive, surface-0 for the two neutral ones. We derive only genericSavings
// vs noSavings; mechanism-specific outcomes need a resolution-outcome field.
export type CaseSummaryOutcome = "negotiation" | "financialAssistance" | "insuranceCoverage" | "genericSavings" | "paymentPlan" | "noSavings" | "userResolved";
const POSITIVE_OUTCOMES: ReadonlySet<CaseSummaryOutcome> = new Set(["negotiation", "financialAssistance", "insuranceCoverage", "genericSavings"]);
type Copy = {
  title: string;
  body: (amount: string) => string;
};
const OUTCOME_COPY: Record<CaseSummaryOutcome, Copy> = {
  negotiation: {
    title: "Congratulations!",
    body: amount => `We negotiated ${amount} off your bill!`
  },
  financialAssistance: {
    title: "Congratulations!",
    body: amount => `We secured you a ${amount} income-based discount!`
  },
  insuranceCoverage: {
    title: "Congratulations!",
    body: amount => `We recovered the ${amount} your insurance owed you!`
  },
  genericSavings: {
    title: "Congratulations!",
    body: amount => `We saved you ${amount} on your hospital bill!`
  },
  paymentPlan: {
    title: "You're set up with a payment plan",
    body: () => "We arranged a manageable plan for your bill."
  },
  noSavings: {
    title: "We reviewed your bill and found no issues",
    body: () => "Good news! You're not being overcharged."
  },
  userResolved: {
    title: "You marked this case resolved",
    body: () => "Glad it's sorted — tell us how we did."
  }
};

// Bucket → outcome: a positive win shows generic savings copy, a $0 win shows
// the no-savings copy.
export function outcomeForBucket(bucket: "saved" | "verified"): CaseSummaryOutcome {
  return bucket === "saved" ? "genericSavings" : "noSavings";
}

// A self-closed win keeps the savings copy when staff recorded savings, but a
// $0 self-closure can't claim "we reviewed your bill" — it gets neutral copy.
export function outcomeForWin(win: {
  bucket: "saved" | "verified";
  userResolved?: boolean;
}): CaseSummaryOutcome {
  if (win.userResolved && win.bucket === "verified") {
    return "userResolved";
  }
  return outcomeForBucket(win.bucket);
}
export type CaseSummaryProps = {
  outcome: CaseSummaryOutcome;
  /** Dollars saved; rendered into the positive copy. Ignored for neutral outcomes. */
  savings?: number;
  onReview: () => void;
  testID?: string;
};
export function CaseSummary({
  outcome,
  savings = 0,
  onReview,
  testID
}: CaseSummaryProps) {
  const media = useMedia();
  const isPositive = POSITIVE_OUTCOMES.has(outcome);
  const amount = formatCurrency(savings, "USD", {
    maximumFractionDigits: 0
  });
  const copy = OUTCOME_COPY[outcome];
  const bg = isPositive ? "$bgPositive" : "$surface0";
  if (!media.gtXs) {
    // Mobile: single summary line + small Review, space-between. Positive shows
    // "$X saved!" in green; neutral shows the title line in default text.
    const mobileText = isPositive ? `${amount} saved!` : copy.title;
    return <XStack testID={testID} bg={bg} ai="center" jc="space-between" px="$sp1.5" py="$sp1" br="$br2" overflow="hidden">
        {isPositive ? <Subtitle1 color="$textPositive">{mobileText}</Subtitle1> : <Body2 color="$textDefault">{mobileText}</Body2>}
        <Button variant="secondary" size="small" fillWidth={false} onPress={onReview}>
          Review
        </Button>
      </XStack>;
  }
  return <XStack testID={testID} bg={bg} ai="center" gap="$sp1" p="$sp2" br="$br2" overflow="hidden" data-sentry-element="XStack" data-sentry-component="CaseSummary" data-sentry-source-file="CaseSummary.tsx">
      <XStack flex={1} fd="column" gap="$sp0.5" jc="center" data-sentry-element="XStack" data-sentry-source-file="CaseSummary.tsx">
        <Subtitle1 color="$textDefault" data-sentry-element="Subtitle1" data-sentry-source-file="CaseSummary.tsx">{copy.title}</Subtitle1>
        <Body1 color="$textDefault" data-sentry-element="Body1" data-sentry-source-file="CaseSummary.tsx">{copy.body(amount)}</Body1>
      </XStack>
      <Button variant="secondary" size="medium" fillWidth={false} onPress={onReview} data-sentry-element="Button" data-sentry-source-file="CaseSummary.tsx">
        Review
      </Button>
    </XStack>;
}